import { fg } from '@atlaskit/platform-feature-flags/fg';

export type NetworkTraceInfo = {
	requestId?: string;
	traceId?: string;
};

const NOT_AVAILABLE = 'not_available';

/**
 * HTTP response headers the gateway returns its trace / request IDs on. `fetchAgg` copies these
 * onto the parsed result as `gatewayTracingHeaders`.
 */
const TRACE_ID_HEADER = 'x-trace-id';
const REQUEST_ID_HEADER = 'atl-request-id';

/**
 * Extracts trace info from an AggResponse-shaped result.
 *
 * AGG stopped returning the `extensions` object on successful responses, so the gateway tracing
 * headers copied off the HTTP response are the reliable source. The `extensions.gateway` body is
 * kept as a fallback for callers (notably Relay) that still receive it.
 */
export const extractTraceInfoFromResponse: (result: unknown) => NetworkTraceInfo | undefined = (
	result: unknown,
): NetworkTraceInfo | undefined => {
	if (!result || typeof result !== 'object') {
		return undefined;
	}

	const preferTracingHeaders = fg('rovo-search-vitalstats-trace-from-headers');

	if (preferTracingHeaders) {
		const tracingHeaders = (result as { gatewayTracingHeaders?: Record<string, string> })
			.gatewayTracingHeaders;
		const traceId = tracingHeaders?.[TRACE_ID_HEADER];
		const requestId = tracingHeaders?.[REQUEST_ID_HEADER];
		if (traceId || requestId) {
			return { requestId, traceId };
		}
	}

	if ('extensions' in result) {
		const extensions = (result as any).extensions;
		const gateway = extensions?.gateway;
		if (gateway) {
			return {
				requestId: gateway.request_id ?? undefined,
				// `tId` is the current gateway field name, `trace_id` the older one.
				traceId: preferTracingHeaders
					? (gateway.tId ?? gateway.trace_id ?? undefined)
					: (gateway.trace_id ?? undefined),
			};
		}
	}
	return undefined;
};

/**
 * Normalises trace info so that missing values are replaced with a sentinel
 * string ('not_available') that is distinguishable from "field not present in
 * the payload" when querying Databricks.
 */
export const normaliseTraceInfo: (traceInfo?: NetworkTraceInfo) => {
	requestId: string;
	traceId: string;
} = (traceInfo?: NetworkTraceInfo) => ({
	requestId: traceInfo?.requestId ?? NOT_AVAILABLE,
	traceId: traceInfo?.traceId ?? NOT_AVAILABLE,
});
